import * as React from 'react'
import classNames from 'classnames'
import { Spinner } from '../../../app-core/lib/ui/Spinner'
import { timeout } from '../../../shared/lib/timeout.no-deps'
import { Auth_loginSync } from './Auth.loginSync'
import { Auth_navigateLogin, Auth_redirectLogin } from './Auth.navigate'
import type { Base_IOperations } from '../Base/Base.IContext'
import { Base_Component } from '../Base/Base.ui.Component'
import { DS_setData, type DS_TData } from '../DS/DS.Data'
import { DS_Listen } from '../DS/DS.ui.Listen'
import { I18N_replaceSlug } from '../I18N/I18N.slugs'
import { Imag_CachedImage } from '../Imag/Imag.ui.CachedImage'
import { LS_customerSet } from '../LS/LS.customer'
import { LS_envSet } from '../LS/LS.env'
import type { ThemePreference } from '../../../shared/domain/serverPreferences/types.no-deps'
import type { Auth_State } from './Auth.State'
import { assertExhaustion } from '../../../shared/lib/typeHelpers.no-deps'
import { getRecentUsers, type User_LoginUser } from '../Devi/Devi.recentUsers'
import { SVG_Chevron } from '../../../app-core/lib/svg/SVG_Chevron'
import {
    initialPromiseState,
    type PromiseState,
    promiseStateRender,
    promiseStateTrigger,
} from '../../../app-core/lib/util/PromiseState'
import type { CommonDeps } from '../../../app-core/lib/base/CommonDeps'
import { groupBy } from 'lodash'
import { Base_commonDeps } from '../Base/Base.commonDeps'
import { Translations } from '../../../shared/i18n/Translations'

const pleaseContinueMessage = () => Translations.t('app:Auth.pleaseContinue')

const logYouOutMessage = () => Translations.t('app:Auth.loggedOut')

type LocationsPreferenceOverride = 'single' | 'multiple' | null

export class Auth_AuthComponent extends Base_Component {
    public data: DS_TData
    public operations: Base_IOperations

    private loginSync = async (locationsPreferenceOverride: LocationsPreferenceOverride) => {
        // Override locations preference in memory during login
        ENV.locationPreferenceOverride = locationsPreferenceOverride

        // Login
        await Auth_loginSync(this.operations.getBus())

        // Persist override after login, so you can reload
        LS_envSet(ENV)
    }

    // switch is not an expression in typescript so we can't inline this in the
    // render function
    private getRenderComponent = (authState: Auth_State, theme: ThemePreference) => {
        switch (authState.status) {
            case 'ready':
                return () => <UserSelect deps={Base_commonDeps()} />
            case 'waiting-for-internet':
                return WaitingForInternet
            case 'pending-automatic-login':
            case 'waiting-for-redirect':
                return WaitingForRedirect
            case 'failure':
                return Failure
            case 'logged-out':
                return LoggedOut
            case 'dev-select':
                return () => <DevSelect loginSync={this.loginSync} />
            case 'pending-sync':
            case 'syncing':
                return () => (
                    <Syncing
                        theme={theme}
                        data={this.data}
                        takingAWhile={'takingAWhile' in authState ? authState.takingAWhile : false}
                    />
                )
            case 'complete':
                // This component shouldn't be mounted if we are in this state
                return null
            default:
                assertExhaustion(authState)
                return null
        }
    }

    public tryRender() {
        return DS_Listen(
            (d) => ({ authState: d.auth, theme: d.serverPrefs.theme }),
            ({ authState, theme }) => {
                const RenderComponent = this.getRenderComponent(authState, theme)
                return (
                    <div className="Auth">
                        <div className="Auth_inner">
                            <RenderComponent />
                        </div>
                    </div>
                )
            }
        )
    }
}

class WaitingForInternet extends React.PureComponent<{}, { spinner: boolean }> {
    public state = {
        spinner: false,
    }

    private onTryAgain = async () => {
        this.setState({ spinner: true })
        await timeout(5000)
        this.setState({ spinner: false })
    }

    public render = () => (
        <div className="Auth_padding">
            <img src="res/no-internet.png" />
            <div className="Auth_internet-message">{Translations.t('app:Auth.pleaseCheckInternet')}</div>
            <div className="Auth_try-again-container">
                {this.state.spinner ? (
                    <div className="AppCore__ui__Pulse" />
                ) : (
                    <button className="Auth_button" onClick={this.onTryAgain}>
                        {Translations.t('app:Auth.tryAgain')}
                    </button>
                )}
            </div>
        </div>
    )
}

const WaitingForRedirect = () => (
    <div className="Auth_padding">
        <div className="Auth_continue-message">{pleaseContinueMessage()}</div>
        <button className="Auth_button" onClick={Auth_navigateLogin}>
            {Translations.t('app:common.continue')}
        </button>
        <div className="Auth_version">{ENV.releaseVersion}</div>
    </div>
)

const Failure = () => (
    <div className="Auth_padding">
        <div className="Auth_failure-message">{Translations.t('app:Auth.somethingFailed')}</div>
        <button className="Auth_button" onClick={Auth_navigateLogin}>
            {Translations.t('app:Auth.somethingFailedButton')}
        </button>
    </div>
)

const LoggedOut = () => (
    <div className="Auth_padding">
        <div className="Auth_failure-message">{logYouOutMessage()}</div>
        <button className="Auth_button" onClick={Auth_navigateLogin}>
            {Translations.t('app:Auth.somethingFailedButton')}
        </button>
    </div>
)

interface DevSelectProps {
    loginSync: (locationPreferenceOverride: LocationsPreferenceOverride) => Promise<void>
}
interface DevSelectState {
    ipAddress: string
    locationsPreferenceOverride: LocationsPreferenceOverride
}
class DevSelect extends React.PureComponent<DevSelectProps, DevSelectState> {
    public state: DevSelectState = {
        ipAddress: '127.0.0.1',
        locationsPreferenceOverride: null,
    }

    private onLocalSelected = async () => {
        const customer = {
            id: -1,
            name: 'local',
            slug: 'local',
            domainName: this.state.ipAddress,
        }
        DS_setData('LocalConnectionSelected', () => ({ customer }))

        await this.props.loginSync(this.state.locationsPreferenceOverride)

        // loginSync clears out the localStorage, so set after it
        LS_customerSet(customer)
    }

    private handleContinueClick = () => this.props.loginSync(this.state.locationsPreferenceOverride)

    public render() {
        return (
            <div className="Auth_dev-select">
                <p className="Auth_dev-select-explaination">
                    This is a development version of Zaptic. If you're not a developer:
                </p>
                <button className="Auth_remote-button" onClick={this.handleContinueClick}>
                    Continue with remote server
                </button>

                <p className="Auth_dev-select-explaination">Or if you're a developer and you want to:</p>

                <input
                    className="Auth_ip-address"
                    type="text"
                    onChange={(event) => this.setState({ ipAddress: event.target.value })}
                    value={this.state.ipAddress}
                />
                <button className="Auth_local-button" onClick={this.onLocalSelected}>
                    Continue with local server
                </button>

                <div className="Auth_locationsPreferenceOverride">
                    <div className="Auth_locationsPreferenceOverride-label">Locations Preference:</div>
                    <div className="Auth_locationsPreferenceOverride-options">
                        <div
                            className={classNames('Auth_locationsPreferenceOverride-option', {
                                selected: this.state.locationsPreferenceOverride === 'single',
                            })}
                            onClick={() => this.setState({ locationsPreferenceOverride: 'single' })}
                        >
                            Single - Manufacturing
                        </div>
                        <div
                            className={classNames('Auth_locationsPreferenceOverride-option', {
                                selected: this.state.locationsPreferenceOverride === 'multiple',
                            })}
                            onClick={() => this.setState({ locationsPreferenceOverride: 'multiple' })}
                        >
                            Multiple - Field Merchandizing
                        </div>
                        <div
                            className={classNames('Auth_locationsPreferenceOverride-option', {
                                selected: this.state.locationsPreferenceOverride == null,
                            })}
                            onClick={() => this.setState({ locationsPreferenceOverride: null })}
                        >
                            Use Server Preference
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}

const Syncing = ({ theme, data, takingAWhile }: { theme: ThemePreference; data: DS_TData; takingAWhile: boolean }) => (
    <div className="Auth_padding">
        {theme && (
            <div>
                <Imag_CachedImage className="Auth_logo" url={theme.logo} />
                <Spinner color={theme.colors.primary} />
            </div>
        )}
        {!theme && <img className="Auth_loader-image" src="res/Process-loader.gif" />}
        <div className="Auth_welcome">
            {theme && theme.loginMessage
                ? I18N_replaceSlug(theme.loginMessage, data)
                : Translations.t('app:Auth.heyHowAreYou')}
        </div>
        <div className="Auth_taking-awhile">
            {takingAWhile && Translations.t('app:Auth.synchronisingMayTakeAWhile')}
        </div>
    </div>
)

interface UserSelectState {
    usersState: PromiseState<User_LoginUser[]>
}
class UserSelect extends React.PureComponent<{ deps: CommonDeps }, UserSelectState> {
    public state: UserSelectState = {
        usersState: initialPromiseState,
    }

    public componentDidMount = async () => {
        promiseStateTrigger(this.props.deps, this, 'usersState', () => getRecentUsers().sortBy('name'))
    }

    public render() {
        return (
            <div className="Auth_user-select">
                <zaptic-icon icon-name="zaptic-logo-full" class="Auth_logo"></zaptic-icon>
                <div className="Auth_welcome">{Translations.t('app:Auth.welcomeBack')}</div>
                <div className="Auth_accounts">
                    {promiseStateRender(this.props.deps, this.state.usersState, (users) =>
                        users.length < 10 ? <BasicUserList users={users} /> : <GroupedUserList users={users} />
                    )}
                </div>
                <div className="Auth_or">{Translations.t('app:common.or')}</div>
                <OtherAccountButton />
            </div>
        )
    }
}
const BasicUserList = ({ users }: { users: User_LoginUser[] }) => (
    <table className="Auth_user-list Auth_basic-user-list">
        {users.map((user) => (
            <AccountButton className="Auth_account-button-simple" key={user.email} user={user} />
        ))}
    </table>
)
const GroupedUserList = ({ users }: { users: User_LoginUser[] }) => {
    const key = (user: User_LoginUser) => user.name[0].toUpperCase()
    const groups = groupBy(users, key)
    return (
        <table className="Auth_user-list">
            {Object.entries(groups).map(([letter, userList]) => (
                <div className="Auth_grouped-user-list">
                    <GroupHeader>{letter}</GroupHeader>
                    {userList.map((user) => (
                        <AccountButton className="Auth_account-button-grouped" key={user.email} user={user} />
                    ))}
                </div>
            ))}
        </table>
    )
}

const GroupHeader = ({ children }: { children: React.ReactChild }) => (
    <div className="Auth_group-user-letter">{children}</div>
)

const AccountButton = ({ user, className }: { user: User_LoginUser; className?: string }) => {
    // we don't worry about revoking this object URL, because as soon as the user selects an account
    // the current document will be unloaded.
    const profileImageUrl = user.profileImageBlob ? URL.createObjectURL(user.profileImageBlob) : undefined

    return (
        <a
            className={classNames('Auth_account-button', className)}
            onClick={() => Auth_redirectLogin(user.email, undefined, 'cancelled-login')}
        >
            <div className="Auth_account-button-inner">
                <zaptic-profile-circle
                    name={user.name}
                    color={user.profileCircleColor}
                    profile-image-url={profileImageUrl}
                ></zaptic-profile-circle>
                <div className="Auth_account-details">
                    <span className="Auth_account-details-name">{user.name}</span>
                    <span className="Auth_account-details-email">{user.email}</span>
                </div>
                <SVG_Chevron className="Auth_account-chevron" />
            </div>
        </a>
    )
}

const OtherAccountButton = () => (
    <a data-testid="other-account-button" className="Auth_signin-other-account" onClick={() => Auth_redirectLogin()}>
        <zaptic-icon icon-name="dashed-plus-icon"></zaptic-icon>
        {Translations.t('app:Auth.otherAccountButton')}
    </a>
)
